// Экран «Проверки» — пятнадцатый экран нового фронта (п.20, 2026-09-07).
// Проблемы графика по сохранённому расчёту: четыре группы (ошибки, конфликты
// ручного ввода, отставание от факта, подсказки), правка прямо в строке,
// снятие замечаний с комментарием и настройка состава и порогов по проекту.
// Логика проверок — checks_lib.py на сервере, здесь только показ и правки.
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { call, p, type Data } from '../api';
import { Pop, at, type Anchor } from '../Pop';
import type { ShellCtx } from '../shell';
import { fmt, parseDate } from '../util';

type ChecksData = Data<'/api/projects/{pid}/report/checks', 'get'>;
type Explain = Data<'/api/tasks/{tid}/explain', 'get'>;

/** строка проверки (поля описаны свободной формой — контракт допускает лишние) */
type Row = {
  klyuch: string; korpus?: string; rabota?: string; etazh?: string | null; pred?: string;
  detali?: string; status?: string | null; rol?: string; task_id?: number; work_id?: number;
  rev?: number; nachalo?: string | null; okonchanie?: string | null;
  nach_vr?: string | null; okonch_vr?: string | null; dlit_r?: number | null;
  brigada?: number | null; podryadchik?: string | null; dlit_w?: number | null; etap_w?: string | null;
  zadachi?: Row[]; vsego?: number; korney?: number; obrezano?: boolean;
  snyato?: { kommentarij?: string; kto?: string; kogda?: string };
};
type Check = {
  kod: string; gruppa: string; vid: string; nazvanie: string; kolvo: number;
  stroki: Row[]; snyatye: Row[]; opisanie?: string; chto_delat?: string;
  porog?: number | null; ed?: string; obrezano?: boolean;
};
type CfgItem = { kod: string; gruppa: string; nazvanie: string; opisanie: string;
  pokazyvat: boolean; porog: number | null; porog_umolch: number | null; ed?: string };

const GROUPS: Record<string, { ico: string; name: string; cls: string }> = {
  'ошибка': { ico: '🔴', name: 'Ошибки — расчёт невозможен или заведомо неверен', cls: 'g-err' },
  'конфликт': { ico: '🟠', name: 'Конфликты ручного ввода', cls: 'g-conf' },
  'отставание': { ico: '🟡', name: 'Отставание от факта', cls: 'g-late' },
  'подсказка': { ico: '⚪', name: 'Подсказки', cls: 'g-hint' },
};
/** правимые поля строки задачи по коду проверки */
const EDIT: Record<string, string[]> = {
  end_before_start: ['nach_vr', 'okonch_vr'], manual_before_link: ['nach_vr', 'okonch_vr'],
  manual_dates_mismatch: ['nach_vr', 'okonch_vr', 'dlit_r'],
  brigade_overlap: ['nach_vr', 'brigada', 'podryadchik'], tk_heat: ['nach_vr'],
  start_overdue: ['nach_vr', 'okonch_vr'], end_overdue_started: ['okonch_vr'],
  duration_outlier: ['dlit_r'], no_dates: ['nach_vr'], front_idle: ['nach_vr'],
  brigade_gap: ['nach_vr'], zero_duration: ['dlit_r'], no_contractor: ['podryadchik'],
};
/** правимые поля строки работы (групповые и справочные проверки) */
const EDIT_W: Record<string, string[]> = { zero_duration: ['dlit_w'], work_no_etap: ['etap_w'] };
type ColDef = { key: string; label: string; date?: boolean; options?: boolean;
  get: (r: Row) => string; manual: (r: Row) => boolean; set: (r: Row, v: string | null) => void };
const COLS: Record<string, ColDef> = {
  nach_vr: { key: 'nachalo_vruchnuyu', label: 'Ручное начало', date: true,
    get: r => fmt(r.nach_vr), manual: r => r.nach_vr != null, set: (r, v) => { r.nach_vr = v; } },
  okonch_vr: { key: 'okonchanie_vruchnuyu', label: 'Ручное оконч.', date: true,
    get: r => fmt(r.okonch_vr), manual: r => r.okonch_vr != null, set: (r, v) => { r.okonch_vr = v; } },
  dlit_r: { key: 'dlit_ruchnaya', label: 'Длит. вручную',
    get: r => String(r.dlit_r ?? ''), manual: r => r.dlit_r != null,
    set: (r, v) => { r.dlit_r = v ? +v.replace(',', '.') : null; } },
  brigada: { key: 'nomer_brigady', label: 'Бригада',
    get: r => String(r.brigada ?? ''), manual: r => r.brigada != null,
    set: (r, v) => { r.brigada = v ? +v.replace(',', '.') : null; } },
  podryadchik: { key: 'podryadchik', label: 'Подрядчик', options: true,
    get: r => r.podryadchik ?? '', manual: r => !!r.podryadchik, set: (r, v) => { r.podryadchik = v; } },
  dlit_w: { key: 'dlitelnost', label: 'Длит. работы, дн',
    get: r => String(r.dlit_w ?? ''), manual: () => false,
    set: (r, v) => { r.dlit_w = v ? +v.replace(',', '.') : null; } },
  etap_w: { key: 'etap', label: 'Этап', options: true,
    get: r => r.etap_w ?? '', manual: () => false, set: (r, v) => { r.etap_w = v; } },
};

export function Checks({ ctx }: { ctx: ShellCtx }) {
  const [data, setData] = useState<ChecksData | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [fixed, setFixed] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});
  const [podr, setPodr] = useState<string[]>([]);
  const [etapy, setEtapy] = useState<string[]>([]);
  const [cfg, setCfg] = useState<CfgItem[] | null>(null);
  const [pop, setPop] = useState<
    | { kind: 'dismiss'; kod: string; klyuch: string; at: Anchor }
    | { kind: 'explain'; row: Row; at: Anchor }
    | null>(null);
  const can = ctx.canPlan;
  const fail = (m: string) => ctx.onError?.(m);
  const bump = () => setData(d => (d ? { ...d } : d));   // перерисовать после локальной правки

  const load = useCallback(async () => {
    setLoading(true);
    const [c, sp] = await Promise.all([
      call<ChecksData>('get', p('/api/projects/{pid}/report/checks', { pid: ctx.pid })),
      call<{ kod: string; znachenie: string }[]>('get', p('/api/projects/{pid}/spiski', { pid: ctx.pid })),
    ]);
    setLoading(false);
    if (!c.ok) { fail(c.error.message); return; }
    setData(c.data);
    setFixed(new Set());
    if (sp.ok) {
      setPodr(sp.data.filter(x => x.kod === 'podryadchik').map(x => x.znachenie).filter(Boolean));
      setEtapy(sp.data.filter(x => x.kod === 'etap').map(x => x.znachenie).filter(Boolean));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.reloadToken]);
  useEffect(() => { void load(); }, [load]);

  const fixKey = (r: Row) => (r.task_id ? 'task:' + r.task_id : 'work:' + r.work_id);
  const isFixed = (r: Row) => fixed.has(fixKey(r));
  const markFixed = (r: Row) => setFixed(prev => new Set(prev).add(fixKey(r)));

  const visible = (r: Row) =>
    (!ctx.matchesWork || ctx.matchesWork(String(r.rabota || ''))) &&
    (!r.korpus || !ctx.matchesKorpus || ctx.matchesKorpus(r.korpus));

  // проверки с учётом фильтров оболочки
  const checks: Check[] = useMemo(() => {
    const src = (data?.proverki || []) as unknown as Check[];
    return src.map(pv => {
      const q: Check = { ...pv };
      if (pv.vid === 'gruppy') {
        q.stroki = pv.stroki
          .map(g => ({ ...g, zadachi: (g.zadachi || []).filter(visible) }))
          .filter(g => (g.zadachi || []).length > 0
            && (!g.korpus || !ctx.matchesKorpus || ctx.matchesKorpus(g.korpus))
            && (g.korpus ? true : (!ctx.matchesWork || ctx.matchesWork(String(g.rabota || '')))));
        q.stroki.forEach(g => {
          g.vsego = (g.zadachi || []).length;
          if ('korney' in g) g.korney = (g.zadachi || []).filter(z => z.rol === 'корень').length;
        });
        q.kolvo = q.stroki.reduce((s, g) => s + (g.zadachi || []).length, 0);
      } else {
        q.stroki = pv.stroki.filter(visible);
        q.kolvo = q.stroki.length;
      }
      q.snyatye = pv.snyatye.filter(visible);
      return q;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ctx.filterKey]);

  const itogo: Record<string, number> = {};
  checks.forEach(c => { itogo[c.gruppa] = (itogo[c.gruppa] || 0) + c.kolvo; });

  async function patchRow(row: Row, col: ColDef, raw: string) {
    let value: string | null = raw.trim() === '' ? null : raw.trim();
    if (col.date && value !== null) {
      const iso = parseDate(value);
      if (iso === undefined) { fail('Дата не разобрана: нужно ДД.ММ.ГГГГ'); return; }
      value = iso;
    }
    const isTask = !!row.task_id;
    const path = isTask ? p('/api/tasks/{tid}', { tid: row.task_id! }) : p('/api/works/{wid}', { wid: row.work_id! });
    const body: Record<string, unknown> = { [col.key]: value };
    if (isTask) body.rev = row.rev;
    const r = await call<{ rev?: number }>('patch', path, body);
    if (!r.ok) { fail(r.error.message); if (r.error.code === 'stale') void load(); return; }
    ctx.onError?.('');
    ctx.onSaved?.();
    col.set(row, value);
    if (r.data && r.data.rev) row.rev = r.data.rev;
    markFixed(row);
    bump();
  }

  async function unfix(row: Row) {
    const r = await call<{ rev?: number }>('patch', p('/api/tasks/{tid}', { tid: row.task_id! }),
      { rev: row.rev, nachalo_vruchnuyu: null, okonchanie_vruchnuyu: null });
    if (!r.ok) { fail(r.error.message); if (r.error.code === 'stale') void load(); return; }
    ctx.onError?.(''); ctx.onSaved?.();
    row.nach_vr = null; row.okonch_vr = null;
    if (r.data && r.data.rev) row.rev = r.data.rev;
    markFixed(row);
    bump();
  }

  async function dismiss(kod: string, klyuch: string, kommentarij: string) {
    const r = await call<{ kogda?: string }>('post', p('/api/projects/{pid}/checks/dismiss', { pid: ctx.pid }),
      { kod, klyuch, kommentarij });
    if (!r.ok) { fail(r.error.message); return; }
    setPop(null); ctx.onError?.('');
    // локально переносим строку в «снятые», чтобы не перечитывать весь ответ
    setData(d => {
      if (!d) return d;
      const copy = JSON.parse(JSON.stringify(d)) as ChecksData;
      const pv = (copy.proverki as unknown as Check[]).find(x => x.kod === kod);
      if (!pv) return copy;
      const snyato = { kommentarij, kto: '', kogda: r.data?.kogda };
      let moved: Row | null = null;
      if (pv.vid === 'gruppy') {
        const gi = pv.stroki.findIndex(g => g.klyuch === klyuch);
        if (gi >= 0) { moved = pv.stroki.splice(gi, 1)[0]; pv.kolvo -= (moved.zadachi || []).length; }
        else pv.stroki.forEach(g => {
          const zi = (g.zadachi || []).findIndex(z => z.klyuch === klyuch);
          if (zi >= 0) {
            moved = g.zadachi!.splice(zi, 1)[0];
            g.vsego = (g.vsego || 1) - 1;
            if ('korney' in g && moved.rol === 'корень') g.korney = (g.korney || 1) - 1;
            pv.kolvo--;
          }
        });
        pv.stroki = pv.stroki.filter(g => (g.zadachi || []).length);
      } else {
        const i = pv.stroki.findIndex(z => z.klyuch === klyuch);
        if (i >= 0) { moved = pv.stroki.splice(i, 1)[0]; pv.kolvo--; }
      }
      if (moved) { (moved as Row).snyato = snyato; pv.snyatye.push(moved); }
      return copy;
    });
  }

  async function undismiss(kod: string, klyuch: string) {
    const r = await call('delete', p('/api/projects/{pid}/checks/dismiss', { pid: ctx.pid }), { kod, klyuch });
    if (!r.ok) { fail(r.error.message); return; }
    void load();
  }

  const toggle = (k: string) => setOpen(prev => {
    const n = new Set(prev);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  const akt = data?.aktual ? new Date(data.aktual) : null;
  const p2 = (n: number) => String(n).padStart(2, '0');
  const meta = (akt
    ? 'Проверки по датам актуализации ' + p2(akt.getDate()) + '.' + p2(akt.getMonth() + 1) + '.'
      + akt.getFullYear() + ' ' + p2(akt.getHours()) + ':' + p2(akt.getMinutes())
    : 'Проект ещё не актуализировался')
    + ' · сегодня ' + fmt(data?.segodnya) + ' · с учётом фильтра сверху'
    + (fixed.size ? ' · исправлено до актуализации: ' + fixed.size : '');

  return (
    <div>
      <div className="bar actbar">
        <span>
          {Object.keys(GROUPS).map(g => (
            <span className="chk-b" key={g}><b>{itogo[g] || 0}</b>{GROUPS[g].ico + ' ' + GROUPS[g].name.split(' — ')[0]}</span>
          ))}
        </span>
        <span className="grow" />
        <button className="sec" title="Перечитать проверки по текущему расчёту" onClick={() => void load()}>Обновить</button>
        {ctx.onRecalc && <button title="Пересчитать даты и заново проверить" onClick={() => ctx.onRecalc?.()}>Пересчитать</button>}
        {can && <button className="sec" title="Какие проверки показывать и с какими порогами"
          onClick={() => void openConfig()}>⚙ Настроить проверки</button>}
      </div>
      <div className="muted">{loading ? 'считаю…' : meta}</div>

      <div>
        {Object.keys(GROUPS).map(g => {
          const ps = checks.filter(c => c.gruppa === g);
          if (!ps.length) return null;
          return (
            <div key={g}>
              <div className="chk-h">{GROUPS[g].ico + ' ' + GROUPS[g].name}</div>
              {ps.map(pv => (
                <details className="chk" key={pv.kod} open={open.has(pv.kod)}>
                  <summary onClick={e => { e.preventDefault(); toggle(pv.kod); }}>
                    <span className={'cnt ' + (pv.kolvo ? GROUPS[g].cls : 'zero')}>{pv.kolvo}</span>
                    <span>{pv.nazvanie}</span>
                    <span className="muted">
                      {(pv.porog != null ? '(порог ' + pv.porog + ' ' + (pv.ed || '') + ')' : '')
                        + (pv.vid === 'gruppy' && pv.kolvo ? ' · групп ' + pv.stroki.length : '')}
                    </span>
                    {pv.snyatye.length > 0 && <span className="muted">· снято {pv.snyatye.length}</span>}
                    {pv.obrezano && <span className="muted" title="Показаны не все строки — уточните фильтр">· показана часть</span>}
                  </summary>
                  {open.has(pv.kod) && (
                    <div className="body">
                      <div className="muted" style={{ margin: '0 0 8px' }}>
                        {(pv.opisanie || '') + ' Что делать: ' + (pv.chto_delat || '')}
                      </div>
                      {pv.kod === 'start_overdue' && pv.kolvo > 0 && (
                        <label className="muted" style={{ display: 'block' }}>
                          <input type="checkbox" checked={!!showAll[pv.kod]}
                            onChange={e => setShowAll(s => ({ ...s, [pv.kod]: e.target.checked }))} />
                          {' '}показывать и следствия (сдвинутся сами)
                        </label>
                      )}
                      {!pv.kolvo && !pv.snyatye.length && <div className="muted">Проблем не найдено.</div>}
                      {pv.kolvo > 0 && (pv.vid === 'spravochnik'
                        ? <WorksTable pv={pv} can={can} podr={podr} etapy={etapy} isFixed={isFixed}
                            onPatch={patchRow} onDismiss={(kod, klyuch, e) => setPop({ kind: 'dismiss', kod, klyuch, at: at(e) })}
                            onGo={ctx.onGoTab} />
                        : <TasksTable pv={pv} can={can} podr={podr} etapy={etapy} isFixed={isFixed}
                            openGroups={open} onToggleGroup={toggle} showAll={!!showAll[pv.kod]}
                            onPatch={patchRow} onUnfix={unfix}
                            onDismiss={(kod, klyuch, e) => setPop({ kind: 'dismiss', kod, klyuch, at: at(e) })}
                            onExplain={(row, e) => setPop({ kind: 'explain', row, at: at(e) })}
                            onCard={n => ctx.onWorkCard?.(n)}
                            onGo={ctx.onGoTab} />)}
                      {pv.snyatye.length > 0 && (
                        <details style={{ marginTop: 8 }}>
                          <summary className="muted" style={{ cursor: 'pointer' }}>Снятые замечания ({pv.snyatye.length})</summary>
                          <table style={{ marginTop: 6 }}>
                            <tbody>
                              {pv.snyatye.map((s, i) => (
                                <tr key={i}>
                                  <td className="left">
                                    {(s.korpus ? s.korpus + ' · ' : '') + (s.rabota || '')
                                      + (s.pred ? ' ← ' + s.pred : '') + (s.etazh ? ' эт. ' + s.etazh : '')}
                                  </td>
                                  <td className="left muted">{s.detali || ''}</td>
                                  <td className="left">{s.snyato?.kommentarij || ''}</td>
                                  <td className="muted">{(s.snyato?.kto ? s.snyato.kto + ' · ' : '') + fmt(s.snyato?.kogda)}</td>
                                  <td className="act">
                                    {can && <a onClick={() => void undismiss(pv.kod, s.klyuch)}>вернуть</a>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </details>
                      )}
                    </div>
                  )}
                </details>
              ))}
            </div>
          );
        })}
      </div>

      {!!(data?.skrytye || []).length && (
        <div className="muted" style={{ marginTop: 10 }}>
          Скрыто проверок: {data!.skrytye.length}
          {can && <> — <a href="#" onClick={e => { e.preventDefault(); void openConfig(); }}>настроить</a></>}
        </div>
      )}
      <div className="muted" style={{ marginTop: 10 }}>
        Проверки считаются по датам последней актуализации. Правка в строке сохраняется сразу (✎),
        даты пересчитаются после «Пересчитать». ⭐ корень — задача, которую нужно править;
        следствия сдвинутся сами. ✕ — снять замечание с комментарием (вернуть можно в блоке «Снятые»).
      </div>

      {cfg && <ConfigWindow items={cfg} counts={Object.fromEntries(checks.map(c => [c.kod, c.kolvo]))}
        onClose={() => setCfg(null)} onSave={saveConfig} />}
      {pop?.kind === 'dismiss' && (
        <DismissPop at={pop.at} onClose={() => setPop(null)}
          onOk={kom => void dismiss(pop.kod, pop.klyuch, kom)} />
      )}
      {pop?.kind === 'explain' && (
        <ExplainPop row={pop.row} at={pop.at} onClose={() => setPop(null)} />
      )}
    </div>
  );

  async function openConfig() {
    const r = await call<CfgItem[]>('get', p('/api/projects/{pid}/checks/config', { pid: ctx.pid }));
    if (!r.ok) { fail(r.error.message); return; }
    setCfg(r.data);
  }
  async function saveConfig(next: Record<string, { pokazyvat: boolean; porog: number | null }>) {
    const r = await call('put', p('/api/projects/{pid}/checks/config', { pid: ctx.pid }), next);
    if (!r.ok) { fail(r.error.message); return; }
    setCfg(null); ctx.onError?.(''); ctx.onSaved?.();
    void load();
  }
}

/** таблица строк-задач (в том числе сгруппированных) */
function TasksTable({ pv, can, podr, etapy, isFixed, openGroups, onToggleGroup, showAll,
  onPatch, onUnfix, onDismiss, onExplain, onGo, onCard }: {
  pv: Check; can: boolean; podr: string[]; etapy: string[]; isFixed: (r: Row) => boolean;
  openGroups: Set<string>; onToggleGroup: (k: string) => void; showAll: boolean;
  onPatch: (row: Row, col: ColDef, v: string) => Promise<void>;
  onUnfix: (row: Row) => Promise<void>;
  onDismiss: (kod: string, klyuch: string, e: React.MouseEvent) => void;
  onExplain: (row: Row, e: React.MouseEvent) => void;
  onGo?: (tab: string, korpus?: string) => void;
  onCard?: (rabota: string) => void;
}) {
  const fields = EDIT[pv.kod] || [];
  const wf = EDIT_W[pv.kod] || [];
  const withRole = pv.kod === 'start_overdue';
  const ncol = 8 + (withRole ? 1 : 0) + fields.length;
  const head = ['Корпус', 'Работа', 'Этаж', 'Начало', 'Оконч.', 'Факт']
    .concat(withRole ? ['Роль'] : []).concat(['Детали'])
    .concat(fields.map(f => COLS[f].label)).concat(['Действия']);

  const taskRow = (z: Row) => (
    <tr key={z.klyuch} className={(z.rol === 'следствие' ? 'sled ' : '') + (isFixed(z) ? 'fixed' : '')}>
      <td>{z.korpus}</td>
      <td className="left pw-link" title="Открыть карточку работы"
        onClick={() => onCard?.(String(z.rabota || ''))}>{z.rabota}</td>
      <td>{z.etazh}</td>
      <td>{fmt(z.nachalo)}</td>
      <td>{fmt(z.okonchanie)}</td>
      <td>{z.status || ''}</td>
      {withRole && <td>{z.rol === 'корень' ? '⭐ корень' : 'следствие'}</td>}
      <td className="left">{z.detali}</td>
      {fields.map(f => z.rol === 'следствие'
        ? <td className="muted" key={f} />
        : <EditCell key={f} row={z} col={COLS[f]} can={can} podr={podr} etapy={etapy} onSave={onPatch} />)}
      {z.rol === 'следствие'
        ? <td className="muted">исправьте корень</td>
        : <ActionsCell pv={pv} row={z} can={can} unfix={fields.includes('nach_vr') || fields.includes('okonch_vr')}
            onUnfix={onUnfix} onDismiss={onDismiss} onExplain={onExplain} onGo={onGo} />}
    </tr>
  );

  return (
    <div className="tbl" style={{ maxHeight: '60vh' }}>
      <table>
        <thead>
          <tr>{head.map((h, i) => <th key={i} className={h === 'Работа' || h === 'Детали' ? 'left' : undefined}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {pv.vid === 'gruppy' ? pv.stroki.map(g => {
            const gk = pv.kod + '|' + g.klyuch;
            const isOpen = openGroups.has(gk);
            let zs = g.zadachi || [];
            if (withRole && !showAll) zs = zs.filter(z => z.rol === 'корень');
            return (
              <Fragment key={gk}>
                <tr className={'gl' + (isFixed(g) ? ' fixed' : '')}>
                  <td colSpan={ncol - wf.length - 1} style={{ cursor: 'pointer' }} onClick={() => onToggleGroup(gk)}>
                    {(isOpen ? '▾ ' : '▸ ') + (g.korpus ? g.korpus + ' · ' : '') + (g.rabota || '')
                      + (g.pred ? ' ← ' + g.pred : '') + ' — ' + (g.detali || '')
                      + (g.obrezano ? ' (показана часть)' : '')}
                  </td>
                  {wf.map(f => <EditCell key={f} row={g} col={COLS[f]} can={can} podr={podr} etapy={etapy} onSave={onPatch} />)}
                  <ActionsCell pv={pv} row={g} can={can} unfix={false}
                    onUnfix={onUnfix} onDismiss={onDismiss} onExplain={onExplain} onGo={onGo} />
                </tr>
                {isOpen && zs.map(taskRow)}
                {isOpen && zs.length < (g.zadachi || []).length && (
                  <tr className="sled">
                    <td colSpan={ncol} className="left">
                      ещё {(g.zadachi || []).length - zs.length} следствий сдвинутся сами (галочка выше — показать)
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          }) : pv.stroki.map(taskRow)}
        </tbody>
      </table>
    </div>
  );
}

/** таблица строк-работ (справочные проверки) */
function WorksTable({ pv, can, podr, etapy, isFixed, onPatch, onDismiss, onGo }: {
  pv: Check; can: boolean; podr: string[]; etapy: string[]; isFixed: (r: Row) => boolean;
  onPatch: (row: Row, col: ColDef, v: string) => Promise<void>;
  onDismiss: (kod: string, klyuch: string, e: React.MouseEvent) => void;
  onGo?: (tab: string, korpus?: string) => void;
}) {
  const wf = EDIT_W[pv.kod] || [];
  return (
    <div className="tbl">
      <table>
        <thead>
          <tr>{['Работа', 'Детали'].concat(wf.map(f => COLS[f].label)).concat(['Действия'])
            .map((h, i) => <th key={i} className="left">{h}</th>)}</tr>
        </thead>
        <tbody>
          {pv.stroki.map(w => (
            <tr key={w.klyuch} className={isFixed(w) ? 'fixed' : undefined}>
              <td className="left">{(w.rabota || '') + (w.pred ? ' ← ' + w.pred : '')}</td>
              <td className="left">{w.detali}</td>
              {wf.map(f => w.work_id
                ? <EditCell key={f} row={w} col={COLS[f]} can={can} podr={podr} etapy={etapy} onSave={onPatch} />
                : <td key={f} />)}
              <ActionsCell pv={pv} row={w} can={can} unfix={false} onDismiss={onDismiss} onGo={onGo} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** ячейка с правкой по клику (дата, число, выпадающий список) */
function EditCell({ row, col, can, podr, etapy, onSave }: {
  row: Row; col: ColDef; can: boolean; podr: string[]; etapy: string[];
  onSave: (row: Row, col: ColDef, v: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(col.get(row));
  useEffect(() => { setV(col.get(row)); }, [row, col]);
  const cls = 'edit' + (col.manual(row) ? ' manual' : '');
  if (!editing) {
    return <td className={can ? cls : ''} title={col.label + (can ? ' — клик для правки' : '')}
      onClick={() => { if (can) setEditing(true); }}>{col.get(row)}</td>;
  }
  const done = async (save: boolean) => {
    setEditing(false);
    if (save && v !== col.get(row)) await onSave(row, col, v); else setV(col.get(row));
  };
  if (col.options) {
    const list = col.key === 'etap' ? etapy : podr;
    return (
      <td>
        <select autoFocus value={v} onChange={e => { setV(e.target.value); }}
          onBlur={() => void done(true)}>
          <option value="" />
          {list.map(x => <option key={x}>{x}</option>)}
          {v && !list.includes(v) && <option>{v}</option>}
        </select>
      </td>
    );
  }
  return (
    <td>
      <input autoFocus value={v} style={{ width: '100%' }} onChange={e => setV(e.target.value)}
        onBlur={() => void done(true)}
        onKeyDown={e => { if (e.key === 'Enter') void done(true); if (e.key === 'Escape') void done(false); }} />
    </td>
  );
}

/** колонка действий: объяснение даты, снять фиксацию, переход на другой экран, снять замечание */
function ActionsCell({ pv, row, can, unfix, onUnfix, onDismiss, onExplain, onGo }: {
  pv: Check; row: Row; can: boolean; unfix: boolean;
  onUnfix?: (row: Row) => Promise<void>;
  onDismiss: (kod: string, klyuch: string, e: React.MouseEvent) => void;
  onExplain?: (row: Row, e: React.MouseEvent) => void;
  onGo?: (tab: string, korpus?: string) => void;
}) {
  const link = linkFor(pv.kod);
  return (
    <td className="act">
      {row.task_id && onExplain && <a title="Как получена дата" onClick={e => onExplain(row, e)}>ⓘ</a>}
      {can && unfix && (row.nach_vr || row.okonch_vr) && onUnfix &&
        <a title="Очистить обе ручные даты задачи" onClick={() => void onUnfix(row)}>снять фиксацию</a>}
      {link && onGo && <a title={link[1]} onClick={() => onGo(link[2], row.korpus)}>{link[0]}</a>}
      {can && <a className="x" title="Снять замечание (с комментарием)"
        onClick={e => onDismiss(pv.kod, row.klyuch, e)}>✕</a>}
    </td>
  );
}
/** куда ведёт ссылка проверки: подпись, подсказка, вкладка */
function linkFor(kod: string): [string, string, string] | null {
  if (kod === 'fact_no_date' || kod === 'fact_before_link') return ['факт', 'Открыть в шахматке факта', 'fakt'];
  if (kod === 'tk_heat' || kod === 'floor_missing') return ['этажи', 'Открыть структуру', 'struct'];
  if (['link_no_pred', 'link_oper_off', 'fact_vs_link', 'links_bad', 'work_no_links'].includes(kod))
    return ['связи', 'Открыть предшественников', 'links'];
  if (['zero_duration', 'work_no_etap', 'no_contractor'].includes(kod))
    return ['работы', 'Открыть справочник работ', 'works'];
  return null;
}

function DismissPop({ at: anchor, onClose, onOk }: { at: Anchor; onClose: () => void; onOk: (kom: string) => void }) {
  const [text, setText] = useState('');
  return (
    <Pop at={anchor} title="Снять замечание" onClose={onClose}>
      <div className="muted" style={{ margin: '4px 0 8px' }}>
        Строка уйдёт в «Снятые» и перестанет считаться. Почему это не ошибка?
      </div>
      <textarea rows={3} style={{ width: '100%', boxSizing: 'border-box' }} autoFocus
        placeholder="Комментарий (необязательно)" value={text} onChange={e => setText(e.target.value)} />
      <div className="bar" style={{ margin: '10px 0 0' }}>
        <button onClick={() => onOk(text.trim())}>Снять</button>
      </div>
    </Pop>
  );
}

/** окно «как получена дата» для строки проверки */
function ExplainPop({ row, at: anchor, onClose }: { row: Row; at: Anchor; onClose: () => void }) {
  const [data, setData] = useState<Explain | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    void (async () => {
      const r = await call<Explain>('get', p('/api/tasks/{tid}/explain', { tid: row.task_id! }));
      if (!r.ok) setErr(r.error.message); else setData(r.data);
    })();
  }, [row.task_id]);
  return (
    <Pop at={anchor} width={460} title={(row.korpus ? row.korpus + ' · ' : '') + (row.rabota || '') + ' · эт. ' + (row.etazh ?? '')}
      onClose={onClose}>
      {err && <div className="pw-err">{err}</div>}
      {!data && !err && <div className="muted">считаю…</div>}
      {data && (
        <div>
          <div className="muted" style={{ margin: '4px 0 8px' }}>
            {data.tip === 'расчёт' ? 'Дата получена расчётом' : 'Дата задана: ' + data.tip}
            {' · '}{fmt(data.nachalo)} — {fmt(data.okonchanie)}
          </div>
          <ul style={{ margin: '0 0 8px 16px', padding: 0 }}>
            {(data.varianty || []).map((v, i) => (
              <li key={i} style={{ fontWeight: v.vybran ? 600 : 400 }}>
                {v.opisanie} → {fmt(v.data)}{v.vybran ? ' ✓' : ''}
              </li>
            ))}
          </ul>
          {(data.istochniki || []).map((s, i) => <div className="muted" key={i}>{s}</div>)}
        </div>
      )}
    </Pop>
  );
}

/** окно настройки состава проверок и порогов */
function ConfigWindow({ items, counts, onClose, onSave }: {
  items: CfgItem[]; counts: Record<string, number>; onClose: () => void;
  onSave: (cfg: Record<string, { pokazyvat: boolean; porog: number | null }>) => Promise<void>;
}) {
  const [state, setState] = useState<Record<string, { pokazyvat: boolean; porog: string }>>(() => {
    const s: Record<string, { pokazyvat: boolean; porog: string }> = {};
    items.forEach(x => { s[x.kod] = { pokazyvat: x.pokazyvat, porog: x.porog == null ? '' : String(x.porog) }; });
    return s;
  });
  const setOne = (kod: string, patch: Partial<{ pokazyvat: boolean; porog: string }>) =>
    setState(s => ({ ...s, [kod]: { ...s[kod], ...patch } }));

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="bar" style={{ margin: '0 0 8px' }}>
        <b>Настройка проверок</b><span className="grow" />
        <span className="muted">общие для всех планеров проекта</span>
      </div>
      <div>
        {Object.keys(GROUPS).map(g => {
          const list = items.filter(x => x.gruppa === g);
          if (!list.length) return null;
          const allOn = list.every(x => state[x.kod]?.pokazyvat);
          return (
            <div key={g}>
              <div className="chk-h" style={{ margin: '10px 0 4px' }}>
                <label>
                  <input type="checkbox" checked={allOn}
                    onChange={e => list.forEach(x => setOne(x.kod, { pokazyvat: e.target.checked }))} />
                </label>
                {' ' + GROUPS[g].ico + ' ' + GROUPS[g].name}
              </div>
              <table>
                <tbody>
                  {list.map(x => (
                    <tr key={x.kod}>
                      <td>
                        <input type="checkbox" checked={!!state[x.kod]?.pokazyvat}
                          onChange={e => setOne(x.kod, { pokazyvat: e.target.checked })} />
                      </td>
                      <td><b>{x.nazvanie}</b><div className="muted">{x.opisanie}</div></td>
                      <td>
                        {x.porog_umolch != null && (
                          <>порог <input type="number" min={0} step={1} style={{ width: 70 }}
                            value={state[x.kod]?.porog ?? ''} onChange={e => setOne(x.kod, { porog: e.target.value })} />
                            {' ' + (x.ed || '')} <span className="muted">(обычно {x.porog_umolch})</span></>
                        )}
                      </td>
                      <td className="muted">{x.kod in counts ? 'сейчас: ' + counts[x.kod] : (x.pokazyvat ? '' : 'скрыта')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
      <div className="bar" style={{ margin: '12px 0 0' }}>
        <button onClick={() => {
          const cfg: Record<string, { pokazyvat: boolean; porog: number | null }> = {};
          Object.entries(state).forEach(([kod, v]) =>
            cfg[kod] = { pokazyvat: v.pokazyvat, porog: v.porog.trim() === '' ? null : +v.porog });
          void onSave(cfg);
        }}>Сохранить</button>
        <button className="sec" title="Все проверки включены, пороги по умолчанию" onClick={() => {
          const s: Record<string, { pokazyvat: boolean; porog: string }> = {};
          items.forEach(x => { s[x.kod] = { pokazyvat: true, porog: x.porog_umolch == null ? '' : String(x.porog_umolch) }; });
          setState(s);
        }}>По умолчанию</button>
        <span className="grow" />
        <button className="sec" onClick={onClose}>Закрыть</button>
      </div>
    </div>
  );
}
